import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';

interface Object_Simple {
    id: number;
    name: string;
}

/**
 * The details of the categories
 */
class ProductDialogStore {
    private sdk: ApplicationSdk;

    public product_image: any = null;
    public product_image_uuid: string | null = null;

    public product_name: string = '';
    public article_number: string = '';
    public product_description: string = '';
    public product_id: number | null = null;
    public product_paid: number = 0;
    public product_image_url: string | null = null;
    public product_category_id: any = null;
    public product_unit: any = null;
    public product_all_objects: number = 1;
    public product_assigned_objects: Set<number> = new Set();
    public product_excluded_objects: Set<number> = new Set();
    // public newCategory: string = '';
    // public categoryQuickSearch: string = '';

    // // public order_id: number | null = null;
    // public categoryNewLoading: boolean = false;
    public categoryDataLoading: boolean = false;
    public unitDataLoading: boolean = false;
    public objectDataLoading: boolean = false;
    public productDataLoading: boolean = false;

    // public categoryDeleteLoading: boolean = false;

    // // public orderData: any = null;
    public categories: Array<any> = [];
    public units: Array<any> = [];

    public assignedObjects: Set<number> = new Set();
    public unassignedObjects: Set<number> = new Set();
    public availableObjects: Map<number, string> = new Map();

    public s3url: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            product_image: observable,
            product_image_uuid: observable,
            product_image_url: observable,
            product_name: observable,
            productName: computed,
            article_number: observable,
            product_assigned_objects: observable,
            product_excluded_objects: observable,
            articleNumber: computed,
            product_id: observable,
            product_description: observable,
            product_category_id: observable,
            product_unit: observable,
            productDescription: computed,
            isPaid: computed,
            switchPaid: action,
            switchProductAllProducts: action,
            product_paid: observable,
            product_all_objects: observable,
            categoryDataLoading: observable,
            unitDataLoading: observable,
            objectDataLoading: observable,
            productDataLoading: observable,
            categories: observable,
            units: observable,
            availableObjects: observable,
            fetchCategories: action,
            selectCategory: action,
            updateObjectAssignment: action,
            calculateAssignedObjects: action,
            selectUnit: action,
            isLoading: computed,
            isUnitLoading: computed,
            productCategory: computed,
            productUnit: computed,
            isAvailableForAll: computed,
            assignedObjects: observable,
            unassignedObjects: observable,
            updateProductAssignment: action,
            clone: action,
            assignedObjectsArray: computed,
            unassignedObjectsArray: computed,
            isCloneable: computed,
            setImage: action,
            image: computed,
            validArticleNumber: computed,
            validTitle: computed,
            validCategory: computed,
            isValid: computed,
            validUnit: computed,
        });
        this.sdk = sdk;
        this.categories = sdk.categories.all;
    }

    public setImage(img: any): void {
        this.product_image = img;
    }

    get image(): any {
        return this.product_image;
    }

    // Clone the current product
    public clone(): void {
        this.product_id = null;
        this.product_name = `${this.product_name} (Kopie)`;
        if (this.product_image_url) {
            this.product_image_uuid = this.product_image_url;
        }
    }

    public async init(config: any): Promise<void> {
        if (config?.product_id) {
            this.product_id = config.product_id;
            await this.fetchProduct(config.product_id);
        } else {
            this.fetchCategories();
            this.fetchObjects();
        }
    }

    public async fetchProduct(id: number): Promise<void> {
        runInAction(() => {
            this.productDataLoading = true;
        });
        const productDetails: any = await this.sdk.fetchApi('product', 'get', { product_id: id });

        runInAction(() => {
            this.productDataLoading = false;
        });

        if (productDetails?.status === 200) {
            runInAction(() => {
                const { product, s3url, category_cache, object_cache } = productDetails.json || {};

                if (product) {
                    this.product_name = product.name;
                    this.article_number = product.article_number;
                    this.product_description = product.description;
                    this.product_paid = product.chargeable;
                    this.product_assigned_objects = new Set(
                        product.assigned_objects
                            .filter((obj: any) => obj.excluded !== true)
                            .map((obj: any) => obj.id)
                    );
                    this.product_excluded_objects = new Set(
                        product.assigned_objects
                            .filter((obj: any) => obj.excluded === true)
                            .map((obj: any) => obj.id)
                    );
                    this.product_all_objects = product.enabled_all_objects;
                    this.product_image_url = product.image;
                    if (product.image) {
                        this.product_image_uuid = product.image;
                    }
                    if (product.category?.id) {
                        this.product_category_id = product.category.id;
                    }
                    if (product.unit?.id) {
                        const unit: any = this.units.find((un: any) => un.id === product.unit.id);
                        if (!unit) {
                            this.units.push({
                                id: product.unit.id,
                                name: `${product.unit.name}`,
                            });
                            this.product_unit = this.units.find(
                                (un: any) => un.id === product.unit.id
                            );
                        }
                    }
                }

                // if we need to update the cache , do it
                if (category_cache) {
                    if (this.sdk.categories.needRefresh(category_cache)) {
                        this.fetchCategories();
                    }
                }

                if (object_cache) {
                    if (this.sdk.objects.needRefresh(object_cache)) {
                        this.fetchObjects();
                    } else {
                        this.updateObjectAssignment(this.sdk.objects.all.data);
                    }
                }

                this.calculateAssignedObjects();
                this.s3url = s3url;
            });
        }
    }

    public async fetchCategories(): Promise<void> {
        runInAction(() => {
            this.categoryDataLoading = true;
        });

        const categoryDetails: any = await this.sdk.fetchApi('category', 'query');

        runInAction(() => {
            this.categoryDataLoading = false;

            if (categoryDetails?.status === 200) {
                this.categories = this.sdk.categories.update(
                    categoryDetails.json.categories,
                    categoryDetails.json.category_cache
                );
            }
        });
    }

    public async fetchUnits(): Promise<void> {
        if (this.units.length > 1) {
            return;
        }
        runInAction(() => {
            this.unitDataLoading = true;
        });

        const unitDetails: any = await this.sdk.fetchApi('product_unit', 'query');

        runInAction(() => {
            this.unitDataLoading = false;

            if (unitDetails?.status === 200) {
                // this.productList = orderListResult.json.data;
                this.units = unitDetails.json.map((unit: any) => {
                    return {
                        id: unit.id,
                        name: `${unit.name}`,
                    };
                });
            }
        });
    }

    public async fetchObjects(): Promise<void> {
        // Reset
        runInAction(() => {
            this.objectDataLoading = true;
            this.assignedObjects = new Set();
            this.unassignedObjects = new Set();
        });

        const objectList: any = await this.sdk.fetchApi('object', 'query', {
            format: 'simple',
            limit: 999,
        });

        runInAction(() => {
            this.objectDataLoading = false;

            if (objectList?.status === 200) {
                const allObjects: any = this.sdk.objects.update(
                    objectList.json.data,
                    objectList.json.object_cache
                );

                this.updateObjectAssignment(allObjects);
                this.calculateAssignedObjects();
            }
        });
    }

    public updateObjectAssignment(allObjects: Array<any> = []): void {
        this.availableObjects.clear();
        allObjects.forEach((object: any) => {
            if (!this.availableObjects.has(object.id)) {
                this.availableObjects.set(object.id, object.name);
            }
        });
    }

    public async onSaveProduct(): Promise<void> {
        runInAction(() => {
            this.productDataLoading = true;
        });
        // 1. - Upload image if it is a new one
        if (this.product_image) {
            const uploadResult = await this.sdk.uploadImage(
                'product',
                'upload',
                this.product_image
            );
            if (uploadResult?.json?.fileId) {
                this.product_image_uuid = uploadResult.json.fileId;
            }
        }

        // 2. - Save the product
        const data = {
            name: this.product_name,
            article_number: this.article_number,
            description: this.product_description,
            category_id: this.product_category_id,
            unit_id: this.product_unit?.id,
            enabled_all_objects: this.product_all_objects,
            chargeable: this.product_paid,
            assigned_objects: Array.from(this.assignedObjects).map((id: number) => id),
            product_id: this.product_id || undefined,
            image: this.product_image_uuid,
        };

        const productRequest: any = await this.sdk.fetchApi(
            'product',
            this.product_id ? 'update' : 'add',
            data
        );

        runInAction(() => {
            this.productDataLoading = true;
        });

        if (productRequest?.status !== 201 && productRequest?.status !== 200) {
            const errorCode = productRequest?.json?.error || '';

            if (errorCode) {
                switch (errorCode) {
                    case 'ERR_PRODUCT_EXISTS':
                        throw new Error('Das Produkt existiert bereits');
                    case 'ERR_PRODUCT_NOT_ADDED':
                        throw new Error('Das Produkt konnte nicht hinzugefügt werden');
                    case 'ERR_VALIDATE_CATEGORY_ID':
                        throw new Error('Bitte wählen Sie eine Kategorie aus');
                    case 'ERR_VALIDATE_NAME':
                        throw new Error('Bitte geben Sie einen Produktnamen ein');
                    case 'ERR_VALIDATE_ARTICLE_NUMBER':
                        throw new Error('Bitte geben Sie eine gültige Artikelnummer ein (1-9999)');
                    default:
                        break;
                }
            }

            throw new Error('Ein unbekannter Fehler ist aufgetreten');
        }
    }

    public selectCategory(category: any): void {
        this.product_category_id = category.id;
    }

    public selectUnit(unit_id: any): void {
        this.product_unit = unit_id;
    }

    public updateProductAssignment(
        unassigned: Array<Object_Simple>,
        assigned: Array<Object_Simple>
    ): void {
        this.assignedObjects = new Set(assigned.map(obj => obj.id));
        this.unassignedObjects = new Set(unassigned.map(obj => obj.id));
    }

    get assignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.assignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?',
            };
        });
    }

    get unassignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.unassignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?',
            };
        });
    }

    set productName(name: string) {
        // limit to 80 characters
        if (name.length <= 80) {
            runInAction(() => {
                this.product_name = name;
            });
        }
    }

    get imageBlob(): string {
        if (!this.product_image_url) {
            return '';
        }
        return `${this.s3url}${this.product_image_url}.jpeg`;
    }

    get productName(): string {
        return this.product_name;
    }

    get productCategory(): any {
        const useCategoryId: number = this.product_category_id || 0;
        return this.sdk.categories.all.find((cat: any) => cat.id === useCategoryId) || null;
    }

    get productUnit(): number {
        return this.product_unit || 0;
    }

    set articleNumber(name: string) {
        // limit to 40 characters
        if (name.length <= 40) {
            runInAction(() => {
                this.article_number = name;
            });
        }
    }

    get articleNumber(): string {
        return this.article_number;
    }

    set productDescription(text: string) {
        // limit to 40 characters
        if (text.length <= 255) {
            runInAction(() => {
                this.product_description = text;
            });
        }
    }

    get isLoading(): boolean {
        return this.productDataLoading;
    }

    get isUnitLoading(): boolean {
        return this.unitDataLoading;
    }

    get productDescription(): string {
        return this.product_description;
    }

    public switchPaid(): void {
        this.product_paid = 1 - this.product_paid;
    }

    public switchProductAllProducts(): void {
        this.product_all_objects = 1 - this.product_all_objects;

        // Recalcualte the assigned and unassigned objects based on the flag and the excluded state
        this.calculateAssignedObjects();
    }

    public calculateAssignedObjects(): void {
        this.assignedObjects.clear();
        this.unassignedObjects.clear();

        if (this.product_all_objects) {
            // Iterate through the available objects and assign all of them
            this.availableObjects.forEach((_name: string, id: number) => {
                if (!this.product_excluded_objects.has(id)) {
                    this.assignedObjects.add(id);
                } else {
                    this.unassignedObjects.add(id);
                }
            });
        } else {
            this.availableObjects.forEach((_name: string, id: number) => {
                // If the object is not excluded, add it to the unassigned objects
                if (!this.product_assigned_objects.has(id)) {
                    this.unassignedObjects.add(id);
                } else {
                    this.assignedObjects.add(id);
                }
            });
        }
    }

    get isPaid(): boolean {
        return !!this.product_paid;
    }

    get isCloneable(): boolean {
        return !!this.product_id && !this.isLoading;
    }

    get isAvailableForAll(): boolean {
        return !!this.product_all_objects;
    }

    get saveButton(): string {
        return this.product_id ? 'Aktualisieren' : 'Erstellen';
    }

    get header(): string {
        return this.product_id ? 'Produkt bearbeiten' : 'Neues Produkt anlegen';
    }

    get id(): string {
        return 'product';
    }

    get validCategory(): boolean {
        return this.productCategory !== null;
    }

    get validUnit(): boolean {
        return !!this.productUnit;
    }

    get validTitle(): boolean {
        return this.product_name.length >= 3 && this.product_name.length < 80;
    }

    get validArticleNumber(): boolean {
        const articleNo = parseInt(this.article_number, 10);
        if (isNaN(articleNo)) {
            return false;
        }
        return articleNo > 0 && articleNo < 10000;
    }

    get isValid() {
        return this.validTitle && this.validArticleNumber && this.validCategory && this.validUnit;
    }
}

export default ProductDialogStore;
